import type { StaticLeadsFilterKey } from "../types/leadsFilterApiCategory";
import type { LeadsDataSource, LeadsFilterBuildContext } from "../types/leadsFilterBuildContext";
import type { LeadsFilterDefinition } from "../types/leadsFilterDefinition";

import { type DefineLeadsFilterInput, defineLeadsFilter } from "./defineLeadsFilter";

/** Ctx fields that are absent until their fetch resolves — the only ones a readiness narrowing can act on. */
type OptionalCtxField = {
    [P in keyof LeadsFilterBuildContext]-?: undefined extends LeadsFilterBuildContext[P] ? P : never;
}[keyof LeadsFilterBuildContext];

/**
 * The data sources that make an optional ctx field present. Deliberately partial: `suppressionLists` and
 * `signals` report readiness from separate signals (`areSuppressionListsReady` / `areSignalsLoading`) and
 * fill fields that are never absent, so there is nothing to narrow — and since the values are constrained to
 * `OptionalCtxField`, adding them here does not compile.
 */
const narrowableSources = {
    description: "description",
    technologies: "technologies",
    industryTaxonomy: "industryTaxonomy",
} as const satisfies Partial<Record<LeadsDataSource, OptionalCtxField>>;

type NarrowableSource = keyof typeof narrowableSources;
type FieldOf<S extends NarrowableSource> = (typeof narrowableSources)[S];

type CtxWith<S extends NarrowableSource> = Omit<LeadsFilterBuildContext, FieldOf<S>> & {
    readonly [P in FieldOf<S>]-?: NonNullable<LeadsFilterBuildContext[P]>;
};

type SourcedFilterInput<K extends StaticLeadsFilterKey, S extends NarrowableSource> = Omit<
    DefineLeadsFilterInput<K>,
    "requires" | "render"
> & {
    readonly render: (ctx: CtxWith<S>) => ReturnType<DefineLeadsFilterInput<K>["render"]>;
};

/**
 * Binds a definition to the one data source its model needs. The requirement is declared once: assembly
 * builds the definition only after that source is in `ready` (`assembleFiltersConfig`'s `isBuildable`), so
 * `render` receives the field resolved and needs no guard of its own.
 *
 * `seedsDefault` and `buildPending` deliberately keep the *wide* ctx — assembly evaluates them before
 * readiness, so narrowing them would be wrong.
 *
 * The `render` cast is the single place the runtime gate is traded for a type. It holds only while `ready`
 * tracks the ctx field's own truthiness within the same render pass; a readiness signal decoupled from the
 * value would invalidate it, which is what the `OptionalCtxField` constraint keeps out of the table above.
 * It goes through `unknown` because `CtxWith<S>` stays deferred while `S` is an unresolved type parameter,
 * so the compiler cannot see the two ctx types overlap.
 */
const defineSourcedFilter =
    <S extends NarrowableSource>(source: S) =>
    <K extends StaticLeadsFilterKey>({ render, ...identity }: SourcedFilterInput<K, S>): LeadsFilterDefinition<K> =>
        defineLeadsFilter({
            ...identity,
            requires: [source],
            render: render as unknown as DefineLeadsFilterInput<K>["render"],
        });

export const defineDescriptionFilter = defineSourcedFilter("description");
export const defineTechnologiesFilter = defineSourcedFilter("technologies");
export const defineIndustryTaxonomyFilter = defineSourcedFilter("industryTaxonomy");
